import { Auth } from "aws-amplify";
import React, { useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import GroupIcon from "mdi-material-ui/Group";
import PeopleIcon from "mdi-material-ui/AccountGroup";
import PlaceIcon from "@material-ui/icons/Place";
import HomeCityIcon from "mdi-material-ui/HomeCity";
import CardAccountIcon from "mdi-material-ui/AccountCardDetails";
import MultipleFilesIcon from "mdi-material-ui/FileDocumentBoxMultiple";
import CreditCardIcon from "mdi-material-ui/CreditCard";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import EventIcon from "@material-ui/icons/EventRounded";
import { withSnackbar } from "notistack";
import { connect } from "react-redux";

import ResponsesList from "../../components/Lists/Responses";
import WarningsList from "../../components/Lists/Warnings";
import BillingContent from "../../content/BillingContent";
import DivisionsContent from "../../content/DivisionsContent";
import MapContent from "../../content/MapContent";
import SchedulingContent from "../../content/SchedulingContent";
import UsersContent from "../../content/UsersContent";
import PropertiesContent from "../../content/PropertiesContent";
import KirsAdminAppBar from "../../components/AppBars/Kirs";
import ReportsContent from "../../content/ReportsContent";
import useIpsExclusive from "../../hooks/useIpsExclusive";
import { isUserRole } from "../../utils/user";
import ClientsContent from "../../content/ClientsContent";

const drawerWidth = 360;

const renderContent = (pathname) => {
  switch (pathname) {
    case "/map":
    case "/map/properties/:propertyId":
    case "/map/properties/create":
    case "/map/responses/:instanceId":
    case "/map/users/:userId":
    case "/map/users/create":
    case "/map/filter":
      return <MapContent />;
    case "/users":
      return <UsersContent />;
    case "/properties":
      return <PropertiesContent />;
    case "/divisions":
    case "/divisions/create":
    case "/divisions/:divisionId":
      return <DivisionsContent />;
    case "/reports":
      return <ReportsContent />;
    case "/schedules":
    case "/schedules/create":
    case "/schedules/:scheduleId":
    case "/scheduling":
      return <SchedulingContent />;
    case "/clients":
      return <ClientsContent />;
    case "/billing":
      return <BillingContent />;
    default:
      console.error(`No match for pathname ${pathname}`);
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100%",
    display: "flex",
  },
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    width: drawerWidth,
  },
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: "0 8px",
    ...theme.mixins.toolbar,
    justifyContent: "flex-start",
  },
  chatDrawer: {
    width: 360,
    position: "absolute",
    bottom: 0,
    left: 0,
    top: 0,
    backgroundColor: "white",
  },
  content: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    position: "relative",
  },
  toolbar: theme.mixins.toolbar,
  scrollable: {
    overflowY: "auto",
  },
}));

let enqueueMessage = () => {};

export const enqueueMessageSnackbar = (messages, propertyId, instanceId) =>
  enqueueMessage(messages, propertyId, instanceId);

const AppContainer = ({
  appError,
  user,
  history,
  match,
  enqueueSnackbar,
  closeSnackbar,
}) => {
  const classes = useStyles();

  const ipsExclusive = useIpsExclusive();

  const drawerListItems = [
    {
      text: "Map",
      icon: <PlaceIcon />,
      pathname: "/map",
    },
    {
      text: "Properties",
      icon: <HomeCityIcon />,
      pathname: "/properties",
    },
    {
      text: "Users",
      icon: <PeopleIcon />,
      pathname: "/users",
    },
    {
      text: "Divisions",
      icon: <GroupIcon />,
      pathname: "/divisions",
    },
    {
      text: "Schedules",
      icon: <EventIcon />,
      pathname: "/schedules",
    },
    {
      text: "Reports",
      icon: <MultipleFilesIcon />,
      pathname: "/reports",
    },
    {
      text: "Clients",
      icon: <CardAccountIcon />,
      pathname: "/clients",
    },
  ];
  if (isUserRole({ admin: true }) && !ipsExclusive) {
    drawerListItems.push({
      text: "Billing",
      icon: <CreditCardIcon />,
      pathname: "billing",
    });
  }

  const messageAction = (key, propertyId, instanceId) => (
    <>
      <Button
        color={"secondary"}
        onClick={() => {
          history.push(`/map/?property=${propertyId}&response=${instanceId}`);
          closeSnackbar();
        }}
      >
        View
      </Button>
      <Button
        color={"secondary"}
        onClick={() => {
          closeSnackbar(key);
        }}
      >
        Dismiss
      </Button>
    </>
  );

  useEffect(() => {
    enqueueMessage = (message, propertyId, instanceId) => {
      enqueueSnackbar(message, {
        action: (key) => messageAction(key, propertyId, instanceId),
        duration: 6000,
      });
    };
  }, []);

  useEffect(() => {
    if (user !== undefined && isUserRole({ officer: true })) {
      const signOut = async () => {
        await Auth.signOut();
        alert("You do not have access to this site.");
      };
      signOut();
    }
  }, [user]);

  useEffect(() => {
    if (appError) {
      enqueueSnackbar(appError, { variant: "error" });
    }
  }, [appError]);

  return (
    <div className={classes.root}>
      <KirsAdminAppBar />
      <Drawer
        className={classes.drawer}
        variant="permanent"
        classes={{
          paper: classes.drawerPaper,
        }}
      >
        <div className={classes.toolbar} />
        <List>
          {drawerListItems.map((item) => (
            <ListItem
              button
              onClick={() => history.push(item.pathname)}
              selected={match.path === item.pathname}
              key={item.text}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItem>
          ))}
        </List>
        <Divider />
        <div className={classes.scrollable}>
          <ResponsesList />
          <WarningsList />
        </div>
      </Drawer>
      <main className={classes.content}>
        <div className={classes.toolbar} />
        {renderContent(match.path)}
      </main>
    </div>
  );
};

const mapStateToProps = (state) => ({
  appError: state.app.globalError,
  user: state.users.byId[state.auth.user.username],
});

export default withSnackbar(connect(mapStateToProps)(AppContainer));
